<template>
    <div>
        <el-dialog :title="title" :visible.sync="dialogVisible" :width="width" :close-on-click-modal="false" :close-on-press-escape="false" :before-close="handleClose" append-to-body>
            <div class="upload-container">
                <div class="drop-zone" @drop="onDrop" @dragover="onDragOver" @dragleave="onDragLeave">
                    <input type="file" ref="fileInput" class="file-input" :multiple="multiple" :accept="accept || 'image/*,.pdf'" @change="onFileSelect" />
                    <div class="drop-content" @click="triggerFileInput">
                        <i class="el-icon-upload"></i>
                        <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
                    </div>
                </div>

                <div class="file-list" v-if="selectedFiles.length > 0">
                    <div class="file-item" v-for="(file, index) in selectedFiles" :key="index">
                        <div class="file-info">
                            <!-- 如果是图片文件，显示缩略图 -->
                            <div class="file-preview" v-if="isImageFile(file)">
                                <img :src="getImagePreviewUrl(file)" @click="previewImage(getImagePreviewUrl(file), file.name)" :alt="file.name" class="thumbnail-img" />
                            </div>

                            <!-- 如果是PDF文件，显示PDF图标 -->
                            <div class="file-preview" v-else-if="isPdfFile(file)">
                                <div class="pdf-thumbnail" @click="previewPdf(getPdfPreviewUrl(file), file.name)">
                                    <i class="el-icon-document"></i>
                                </div>
                            </div>

                            <div class="file-text">
                                <span class="file-name" @click="getFilePreviewHandler(file)" :class="{ 'image-file': isImageFile(file), 'pdf-file': isPdfFile(file) }">
                                    {{ file.name }}
                                </span>
                                <span class="file-size">{{ formatFileSize(file.size) }}</span>
                            </div>
                        </div>
                        <el-button type="text" icon="el-icon-delete" class="remove-btn" @click="removeFile(index)"></el-button>
                    </div>
                </div>

                <div class="el-upload__tip" v-if="tip" slot="tip">{{ tip }}</div>
            </div>

            <!-- 图片预览对话框 -->
            <el-dialog :visible.sync="previewVisible" :append-to-body="true" width="80%" :modal-append-to-body="false" :close-on-press-escape="true" :fullscreen="isFullscreen">
                <div class="image-preview-container">
                    <div class="image-preview-header">
                        <span class="image-title">{{ previewImageTitle }}</span>
                        <div class="controls" v-if="currentFileType === 'image'">
                            <el-button type="text" icon="el-icon-full-screen" @click="toggleFullscreen" class="control-btn"></el-button>
                            <el-button type="text" icon="el-icon-zoom-in" @click="zoomIn" class="control-btn"></el-button>
                            <el-button type="text" icon="el-icon-zoom-out" @click="zoomOut" class="control-btn"></el-button>
                            <el-button type="text" icon="el-icon-refresh-left" @click="rotateLeft" class="control-btn"></el-button>
                            <el-button type="text" icon="el-icon-refresh-right" @click="rotateRight" class="control-btn"></el-button>
                            <span class="zoom-value">{{ zoomLevel }}%</span>
                        </div>
                    </div>

                    <!-- 图片预览 -->
                    <div class="image-preview-wrapper" v-if="currentFileType === 'image'">
                        <img :src="previewImageUrl" class="preview-image" :style="{transform: `scale(${zoomLevel/100}) rotate(${rotation}deg)`,transition: 'transform 0.3s ease'}" @click="toggleFullscreen" />
                    </div>

                    <!-- PDF预览 -->
                    <div class="pdf-preview-wrapper" v-else-if="currentFileType === 'pdf'">
                        <iframe :src="previewPdfUrl" class="preview-pdf" width="100%" height="600px"></iframe>
                    </div>

                    <div class="image-nav" v-if="imageFiles.length > 1 && currentFileType === 'image'">
                        <el-button type="text" icon="el-icon-arrow-left" @click="prevImage" class="nav-btn prev-btn"></el-button>
                        <el-button type="text" icon="el-icon-arrow-right" @click="nextImage" class="nav-btn next-btn"></el-button>

                        <div class="image-thumbnails">
                            <div v-for="(img, idx) in imageFiles" :key="idx" :class="['thumb-item', { active: currentImageIndex === idx }]" @click="switchImage(idx)">
                                <img :src="getImagePreviewUrl(img)" :alt="img.name" />
                            </div>
                        </div>
                    </div>
                </div>
            </el-dialog>

            <div slot="footer" class="dialog-footer">
                <el-button @click="cancelUpload">取 消</el-button>
                <el-button type="primary" @click="confirmUpload">确 定</el-button>
                <el-button type="primary" @click="clearFiles" v-if="clearBtn">清空列表</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
/**
 * 拖拽上传组件（使用原生input实现）
 * 封装了Element UI的el-dialog功能，使用原生input实现文件上传
 */
export default {
    name: 'DragUpload',
    props: {
        // 对话框标题
        title: {
            type: String,
            default: '文件上传'
        },
        // 上传类型标识，随上传接口参数一起提交
        uploadType: {
            type: String,
            default: ''
        },
        // 对话框宽度
        width: {
            type: String,
            default: '600px'
        },
        // 是否支持多选
        multiple: {
            type: Boolean,
            default: true
        },
        // 文件数量限制
        limit: {
            type: Number,
            default: 10
        },
        // 接受上传的文件类型
        accept: {
            type: String,
            default: ''
        },
        // 提示文字
        tip: {
            type: String,
            default: ''
        },
        // 显示清空按钮
        clearBtn: {
            type: Boolean,
            default: false
        },
        // 是否只上传不调用接口
        scanFlag: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            dialogVisible: false,
            selectedFiles: [],
            previewVisible: false,
            previewImageUrl: '',
            previewPdfUrl: '',
            previewImageTitle: '',
            isFullscreen: false,
            zoomLevel: 100,
            rotation: 0,
            currentImageIndex: 0,
            currentFileType: 'image' // 'image' 或 'pdf'
        };
    },

    computed: {
        imageFiles() {
            // 返回所有图片文件
            return this.selectedFiles.filter(file => this.isImageFile(file));
        }
    },

    methods: {
        /**
         * 打开上传对话框
         */
        open() {
            this.dialogVisible = true;
            this.selectedFiles = [];
        },

        /**
         * 触发文件选择输入框
         */
        triggerFileInput() {
            this.$refs.fileInput.click();
        },

        /**
         * 处理文件选择
         */
        onFileSelect(event) {
            const files = Array.from(event.target.files);
            this.addFiles(files);
            // 清空input值，允许重复选择相同文件
            event.target.value = '';
        },

        /**
         * 添加文件到列表
         */
        addFiles(files) {
            // 检查文件数量限制
            if (this.selectedFiles.length + files.length > this.limit) {
                const allowedCount = this.limit - this.selectedFiles.length;
                if (allowedCount <= 0) {
                    this.$message.warning(`最多只能上传 ${this.limit} 个文件`);
                    return;
                }
                files = files.slice(0, allowedCount);
            }

            // 过滤并添加文件
            files.forEach(file => {
                // 检查是否已存在相同文件名
                const exists = this.selectedFiles.some(f => f.name === file.name && f.size === file.size);
                if (!exists) {
                    this.selectedFiles.push(file);
                }
            });

            if (files.length > 0) {
                this.$emit('files-selected', this.selectedFiles);
            }
        },

        /**
         * 判断是否为图片文件
         */
        isImageFile(file) {
            if (!file) return false;
            const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/bmp', 'image/webp'];
            return imageTypes.includes(file.type);
        },

        /**
         * 判断是否为PDF文件
         */
        isPdfFile(file) {
            if (!file) return false;
            return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        },

        /**
         * 获取文件预览处理器
         */
        getFilePreviewHandler(file) {
            if (this.isImageFile(file)) {
                this.previewImage(this.getImagePreviewUrl(file), file.name);
            } else if (this.isPdfFile(file)) {
                this.previewPdf(this.getPdfPreviewUrl(file), file.name);
            }
        },

        /**
         * 获取图片预览URL
         */
        getImagePreviewUrl(file) {
            if (!file || !this.isImageFile(file)) return '';
            return URL.createObjectURL(file);
        },

        /**
         * 获取PDF预览URL
         */
        getPdfPreviewUrl(file) {
            if (!file || !this.isPdfFile(file)) return '';
            return URL.createObjectURL(file);
        },

        /**
         * 预览图片
         */
        previewImage(imgUrl, title) {
            this.previewImageUrl = imgUrl;
            this.previewImageTitle = title;
            this.currentImageIndex = this.imageFiles.findIndex(file => this.getImagePreviewUrl(file) === imgUrl);
            this.previewVisible = true;
            this.currentFileType = 'image';
            // 重置缩放和旋转
            this.resetView();
        },

        /**
         * 预览PDF
         */
        previewPdf(pdfUrl, title) {
            this.previewPdfUrl = pdfUrl;
            this.previewImageTitle = title;
            this.previewVisible = true;
            this.currentFileType = 'pdf';
        },

        /**
         * 重置视图参数
         */
        resetView() {
            this.zoomLevel = 100;
            this.rotation = 0;
        },

        /**
         * 切换全屏
         */
        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
        },

        /**
         * 放大图片
         */
        zoomIn() {
            if (this.zoomLevel < 200) {
                this.zoomLevel += 20;
            }
        },

        /**
         * 缩小图片
         */
        zoomOut() {
            if (this.zoomLevel > 20) {
                this.zoomLevel -= 20;
            }
        },

        /**
         * 向左旋转
         */
        rotateLeft() {
            this.rotation -= 90;
        },

        /**
         * 向右旋转
         */
        rotateRight() {
            this.rotation += 90;
        },

        /**
         * 上一张图片
         */
        prevImage() {
            if (this.currentImageIndex > 0) {
                this.currentImageIndex--;
            } else {
                this.currentImageIndex = this.imageFiles.length - 1;
            }
            this.switchImage(this.currentImageIndex);
        },

        /**
         * 下一张图片
         */
        nextImage() {
            if (this.currentImageIndex < this.imageFiles.length - 1) {
                this.currentImageIndex++;
            } else {
                this.currentImageIndex = 0;
            }
            this.switchImage(this.currentImageIndex);
        },

        /**
         * 切换到指定图片
         */
        switchImage(index) {
            if (index >= 0 && index < this.imageFiles.length) {
                this.currentImageIndex = index;
                this.previewImageUrl = this.getImagePreviewUrl(this.imageFiles[index]);
                this.previewImageTitle = this.imageFiles[index].name;
                this.resetView();
            }
        },

        /**
         * 处理拖拽放置事件
         */
        onDrop(event) {
            event.preventDefault();
            const dropZone = event.currentTarget;
            dropZone.classList.remove('drag-over');

            const files = Array.from(event.dataTransfer.files);
            this.addFiles(files);
        },

        /**
         * 处理拖拽悬停事件
         */
        onDragOver(event) {
            event.preventDefault();
            const dropZone = event.currentTarget;
            dropZone.classList.add('drag-over');
        },

        /**
         * 处理拖拽离开事件
         */
        onDragLeave(event) {
            const dropZone = event.currentTarget;
            dropZone.classList.remove('drag-over');
        },

        /**
         * 移除指定文件
         */
        removeFile(index) {
            // 释放之前创建的对象URL
            const removedFile = this.selectedFiles[index];
            if (removedFile && this.isImageFile(removedFile)) {
                URL.revokeObjectURL(this.getImagePreviewUrl(removedFile));
            } else if (removedFile && this.isPdfFile(removedFile)) {
                URL.revokeObjectURL(this.getPdfPreviewUrl(removedFile));
            }
            this.selectedFiles.splice(index, 1);

            // 如果删除的是当前预览的文件，关闭预览
            if (this.previewVisible && this.currentFileType === 'pdf') {
                this.previewVisible = false;
            } else if (this.previewVisible && this.imageFiles.length === 0) {
                this.previewVisible = false;
            }
        },

        /**
         * 格式化文件大小
         */
        formatFileSize(size) {
            if (size === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(this.log(size) / this.log(k));
            return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        // 定义log函数，因为Math.log不是内置函数
        log(x) {
            return Math.log(x);
        },

        /**
         * 关闭对话框
         */
        handleClose(done) {
            this.$confirm('确认关闭？文件上传将会取消')
                .then(_ => {
                    this.clearFiles();
                    done();
                })
                .catch(_ => { });
        },

        /**
         * 取消上传
         */
        cancelUpload() {
            this.dialogVisible = false;
            this.clearFiles();
        },

        /**
         * 确定上传
         */
        async confirmUpload() {
            if (this.selectedFiles.length === 0) {
                this.$message.warning('请至少选择一个文件');
                return;
            }
            if (this.scanFlag) {
                console.log(this.selectedFiles);
                this.$emit('confirm', this.selectedFiles);
                this.dialogVisible = false;
                return;
            }
            // 上传文件
            const results = [];
            for (const file of this.selectedFiles) {
                try {
                    const params = {
                        file: file
                    };
                    if (this.uploadType) {
                        params.type = this.uploadType;
                    }
                    const response = await this.$http.uploadFile(params, true);
                    if (response.status == 200) {
                        results.push(response.result[0]);
                    } else {
                        this.$message.error(response.message);
                    }
                } catch (error) {
                    this.$message.error(`文件 ${file.name} 上传失败: ${error.message}`);
                    return;
                }
            }

            this.$emit('confirm', results);
            this.dialogVisible = false;
        },

        /**
         * 清空文件列表
         */
        clearFiles() {
            // 释放所有图片预览URL
            this.selectedFiles.forEach(file => {
                if (this.isImageFile(file)) {
                    URL.revokeObjectURL(this.getImagePreviewUrl(file));
                } else if (this.isPdfFile(file)) {
                    URL.revokeObjectURL(this.getPdfPreviewUrl(file));
                }
            });
            this.selectedFiles = [];
        }
    }
};
</script>

<style scoped>
.upload-container {
    text-align: center;
}

.drop-zone {
    position: relative;
    border: 2px dashed #c0ccda;
    border-radius: 6px;
    padding: 40px;
    text-align: center;
    transition: all 0.3s;
    cursor: pointer;
    margin-bottom: 20px;
}

.drop-zone:hover {
    border-color: #409eff;
}

.drop-zone.drag-over {
    border-color: #409eff;
    background-color: rgba(64, 158, 255, 0.05);
}

.file-input {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    cursor: pointer;
}

.drop-content {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.drop-content i {
    font-size: 67px;
    color: #c0ccda;
    margin-bottom: 16px;
}

.drop-content .el-upload__text {
    color: #666;
    font-size: 14px;
    margin: 0 0 16px;
}

.drop-content .el-upload__text em {
    color: #409eff;
    font-weight: bold;
}

.file-list {
    max-height: 400px;
    overflow-y: auto;
    text-align: left;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 10px;
    margin-top: 10px;
}

.file-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #eee;
}

.file-item:last-child {
    border-bottom: none;
}

.file-info {
    display: flex;
    flex: 1;
    align-items: center;
}

.file-preview {
    width: 60px;
    height: 60px;
    margin-right: 10px;
    flex-shrink: 0;
    border-radius: 4px;
    overflow: hidden;
    border: 1px solid #dcdfe6;
}

.thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: pointer;
    transition: transform 0.3s;
}

.thumbnail-img:hover {
    transform: scale(1.05);
}

.pdf-thumbnail {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f5f7fa;
}

.pdf-thumbnail i {
    font-size: 30px;
    color: #e6a23c;
}

.file-text {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.file-name {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 10px;
    cursor: pointer;
    color: #606266;
    max-width: 100%;
}

.image-file {
    color: #409eff;
}

.pdf-file {
    color: #e6a23c;
}

.file-name:hover {
    text-decoration: underline;
}

.file-size {
    color: #909399;
    font-size: 12px;
}

.remove-btn {
    color: #f56c6c;
}

.remove-btn:hover {
    color: #f56c6c;
}

.upload-drag /deep/ .el-upload__tip {
    margin-top: 10px;
    padding: 0;
    color: #999;
    font-size: 13px;
}

/* 图片预览样式 */
.image-preview-container {
    position: relative;
}

.image-preview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 10px;
}

.image-title {
    font-weight: bold;
    color: #333;
    flex: 1;
}

.controls {
    display: flex;
    align-items: center;
}

.control-btn {
    color: #606266;
    margin-left: 10px;
    font-size: 16px;
}

.control-btn:hover {
    color: #409eff;
}

.zoom-value {
    margin-left: 10px;
    color: #606266;
}

.image-preview-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
    overflow: hidden;
}

.preview-image {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    cursor: zoom-in;
    transition: transform 0.3s ease;
}

.pdf-preview-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
    overflow: hidden;
}

.preview-pdf {
    width: 100%;
    min-height: 600px;
}

.image-nav {
    position: relative;
    margin-top: 15px;
}

.nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 24px;
    color: #fff;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    padding: 0;
    z-index: 10;
}

.prev-btn {
    left: 0;
}

.next-btn {
    right: 0;
}

.image-thumbnails {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 40px;
}

.thumb-item {
    width: 60px;
    height: 60px;
    border: 2px solid transparent;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    transition: border-color 0.3s;
}

.thumb-item.active {
    border-color: #409eff;
}

.thumb-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
</style>
<!-- 
    <template>
  <div>
    <el-button @click="showUploadDialog">打开上传对话框</el-button>
    <DragUpload 
      ref="dragUpload"
      title="图片上传"
      :multiple="true"
      :limit="5"
      @confirm="onUploadConfirm"
    />
  </div>
</template>

<script>
import DragUpload from '@/byComponents/DragUpload.vue';

export default {
  components: {
    DragUpload
  },
  data() {
  },
  methods: {
    showUploadDialog() {
      this.$refs.dragUpload.open();
    },
    onUploadConfirm(files) {
      console.log('上传的文件:', files);
    }
  }
};
</script>
-->